/**
 * API Keys management page — lets users create/delete HMAC API keys
 * for programmatic access to their account data and order submission.
 *
 * Keys are bound to the connected wallet address.  The raw key is shown
 * exactly once at creation time and is never stored server-side (we keep
 * only the SHA-256 hash).
 */
import React, { useState, useEffect, useCallback } from "react";
import { t, Trans } from "@lingui/macro";
import { ethers } from "ethers";

import { INDEXER_BASE } from "lib/indexer/client";
import useWallet from "lib/wallets/useWallet";
import { useEthersSigner } from "lib/wallets/useEthersSigner";
import { useChainId } from "lib/chains";
import { helperToast } from "lib/helperToast";

import "./ApiKeys.css";

type ApiKey = {
  id: string;
  label: string;
  created_at: number;
  last_used_at: number | null;
};

function fmt(ts: number | null): string {
  if (!ts) return "—";
  return new Date(ts * 1000).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** SHA-256 of a string → lowercase hex */
async function sha256Hex(str: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Generate a cryptographically random API key (mk_ + 40 random hex bytes) */
function generateRawKey(): string {
  const bytes = new Uint8Array(40);
  crypto.getRandomValues(bytes);
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return `mk_${hex}`;
}

export default function ApiKeysPage() {
  const { active, account } = useWallet();
  const signer = useEthersSigner();
  const { chainId } = useChainId();

  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [labelInput, setLabelInput] = useState("");
  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null); // shown once
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchKeys = useCallback(async () => {
    if (!account) return;
    setLoading(true);
    try {
      const res = await fetch(`${INDEXER_BASE}/api/keys?address=${account.toLowerCase()}`);
      const data = await res.json();
      setKeys(data.keys || []);
    } catch {
      helperToast.error(t`Failed to load API keys`);
    } finally {
      setLoading(false);
    }
  }, [account]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  /** Sign ownership proof message */
  const signProof = useCallback(async (): Promise<{ sig: string; timestamp: number } | null> => {
    if (!signer) return null;
    const timestamp = Math.floor(Date.now() / 1000);
    try {
      const sig = await signer.signMessage(`MoneyX API Key: ${timestamp}`);
      return { sig, timestamp };
    } catch {
      helperToast.error(t`Signature rejected`);
      return null;
    }
  }, [signer]);

  const handleCreate = useCallback(async () => {
    if (!account || !signer) return;
    const proof = await signProof();
    if (!proof) return;

    const rawKey = generateRawKey();
    const key_hash = await sha256Hex(rawKey);

    setCreating(true);
    try {
      const res = await fetch(`${INDEXER_BASE}/api/keys`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          address: account.toLowerCase(),
          label: labelInput.trim() || "Unnamed key",
          key_hash,
          sig: proof.sig,
          timestamp: proof.timestamp,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        helperToast.error(data.error || t`Failed to create key`);
        return;
      }
      setNewKey(rawKey);
      setLabelInput("");
      await fetchKeys();
    } catch {
      helperToast.error(t`Failed to create key`);
    } finally {
      setCreating(false);
    }
  }, [account, signer, labelInput, signProof, fetchKeys]);

  const handleDelete = useCallback(
    async (id: string) => {
      if (!account || !signer) return;
      const proof = await signProof();
      if (!proof) return;

      setDeletingId(id);
      try {
        const res = await fetch(`${INDEXER_BASE}/api/keys/${id}`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            address: account.toLowerCase(),
            sig: proof.sig,
            timestamp: proof.timestamp,
          }),
        });
        if (!res.ok) {
          const data = await res.json();
          helperToast.error(data.error || t`Delete failed`);
          return;
        }
        await fetchKeys();
        helperToast.success(t`API key deleted`);
      } catch {
        helperToast.error(t`Delete failed`);
      } finally {
        setDeletingId(null);
      }
    },
    [account, signer, signProof, fetchKeys]
  );

  if (!active) {
    return (
      <div className="AK-page">
        <div className="AK-connect-prompt">
          <Trans>Connect your wallet to manage API keys.</Trans>
        </div>
      </div>
    );
  }

  return (
    <div className="AK-page">
      <div className="AK-container">
        <h1 className="AK-heading"><Trans>API Keys</Trans></h1>
        <p className="AK-subheading">
          <Trans>
            Create API keys for programmatic access to your positions, orders, and account data.
            Include your key in requests as the <code>X-Api-Key</code> header.
            Keys are tied to your wallet address.
          </Trans>
        </p>

        {/* One-time key reveal */}
        {newKey && (
          <div className="AK-reveal">
            <div className="AK-reveal-header">
              <span className="AK-reveal-title">
                ✓ <Trans>Key created — copy it now, it will not be shown again</Trans>
              </span>
              <button className="AK-reveal-close" onClick={() => setNewKey(null)}>✕</button>
            </div>
            <div className="AK-reveal-key">
              <code>{newKey}</code>
              <button
                className="AK-copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(newKey);
                  helperToast.success(t`Copied to clipboard`);
                }}
              >
                <Trans>Copy</Trans>
              </button>
            </div>
          </div>
        )}

        {/* Create form */}
        <div className="AK-create-section">
          <h2 className="AK-section-title"><Trans>Create New Key</Trans></h2>
          <div className="AK-create-row">
            <input
              className="AK-input"
              placeholder={t`Label (e.g. "Trading bot")`}
              value={labelInput}
              onChange={(e) => setLabelInput(e.target.value)}
              maxLength={64}
              disabled={creating}
            />
            <button
              className="AK-btn-create"
              onClick={handleCreate}
              disabled={creating || keys.length >= 10}
            >
              {creating ? t`Creating…` : t`+ Create Key`}
            </button>
          </div>
          {keys.length >= 10 && (
            <p className="AK-warn"><Trans>Maximum 10 keys per address.</Trans></p>
          )}
        </div>

        {/* Key list */}
        <div className="AK-list-section">
          <h2 className="AK-section-title">
            <Trans>Your Keys</Trans>
            {!loading && <span className="AK-count"> ({keys.length})</span>}
          </h2>

          {loading && <div className="AK-loading"><Trans>Loading…</Trans></div>}

          {!loading && keys.length === 0 && (
            <div className="AK-empty"><Trans>No API keys yet. Create one above.</Trans></div>
          )}

          {!loading && keys.length > 0 && (
            <table className="AK-table">
              <thead>
                <tr>
                  <th><Trans>Label</Trans></th>
                  <th><Trans>Created</Trans></th>
                  <th><Trans>Last Used</Trans></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id}>
                    <td className="AK-td-label">{key.label || "—"}</td>
                    <td className="AK-td-date">{fmt(key.created_at)}</td>
                    <td className="AK-td-date">{fmt(key.last_used_at)}</td>
                    <td className="AK-td-action">
                      <button
                        className="AK-btn-delete"
                        onClick={() => handleDelete(key.id)}
                        disabled={deletingId === key.id}
                      >
                        {deletingId === key.id ? t`Deleting…` : t`Revoke`}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Usage docs */}
        <div className="AK-docs">
          <h2 className="AK-section-title"><Trans>Usage</Trans></h2>
          <pre className="AK-code-block">{`# Authenticate with your API key
curl https://api.moneyxpro.com/api/account/${account?.toLowerCase()}/stats \\
  -H "X-Api-Key: mk_your_key_here"

# List open positions
curl https://api.moneyxpro.com/api/positions?address=${account?.toLowerCase()} \\
  -H "X-Api-Key: mk_your_key_here"`}</pre>
        </div>
      </div>
    </div>
  );
}
